import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { Group } from 'three';

interface MinecraftCharacterEquipProps {
    modelUrl: string;
    modelCategory: string;
}

const MinecraftCharacterEquip: React.FC<MinecraftCharacterEquipProps> = ({ modelUrl, modelCategory }) => {
    const meshRef = useRef<Group>(null);

    // 고정된 위치, 회전, 스케일 값
    const fixedValues: Record<string, { position: [number, number, number], rotation: [number, number, number], scale: [number, number, number] }> = {
        'Head': { position: [-0.5, 0.25, -0.5], rotation: [0, 0, 0], scale: [1, 1, 1] },
        'rightHand': { position: [0, 0.09, -0.3], rotation: [-90, 0, 0], scale: [1, 1, 1] },
        'leftHand': { position: [0.06, 0.1, -0.3], rotation: [-90, 0, 0], scale: [1, 1, 1] }
    };

    useEffect(() => {
        const loader = new GLTFLoader();

        // 모델 로드 함수
        const loadModel = (url: string): Promise<GLTF> => {
            console.log(`Loading model from: ${url}`);
            return new Promise((resolve, reject) => {
                loader.load(url, (gltf) => {
                    console.log(`Model loaded from: ${url}`);
                    resolve(gltf);
                }, undefined, (error) => {
                    console.error(`Error loading model from: ${url}`, error);
                    reject(error);
                });
            });
        };

        // 장비 모델 부착 함수
        const attachEquipment = (
            model: THREE.Object3D,
            equipmentUrl: string,
            boneName: string
        ) => {
            console.log(`Attaching equipment from: ${equipmentUrl}`);
            loadModel(equipmentUrl)
                .then((equipmentGltf) => {
                    console.log(`Equipment loaded from: ${equipmentUrl}`);
                    const equipmentModel = equipmentGltf.scene;

                    const bone = model.getObjectByName(boneName);

                    if (bone) {
                        bone.add(equipmentModel);

                        // 장착할 위치와 회전 설정
                        const { position, rotation, scale } = fixedValues[boneName] || { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] };

                        // 장착할 모델의 위치를 조정
                        equipmentModel.position.set(...position);

                        // Rotation 설정 시 각도를 라디안으로 변환
                        const rotationRadians = rotation.map(deg => THREE.MathUtils.degToRad(deg)) as [number, number, number];
                        equipmentModel.rotation.set(...rotationRadians);

                        equipmentModel.scale.set(...scale);

                        // 'Hand'라는 이름의 메쉬를 찾아 비활성화
                        const hiddenObject = equipmentModel.getObjectByName('Hand');
                        if (hiddenObject) {
                            hiddenObject.visible = false; // 해당 오브젝트를 보이지 않게 설정
                        }
                    } else {
                        console.warn(`Bone with name ${boneName} not found.`);
                    }
                })
                .catch((error) => console.error(`Error loading equipment model: ${error}`));
        };

        // 비동기 IIFE 함수 실행
        (async () => {
            try {
                console.log(`Loading character model`);
                const gltf = await loadModel(`${process.env.PUBLIC_URL}/assets/BlackModel.glb`);  // 기본 캐릭터 모델
                const avatarModel = gltf.scene;

                avatarModel.scale.set(2.5, 2.5, 2.5);
                avatarModel.position.set(0, -3.2, 4);

                console.log(`Character model loaded successfully`);

                // 텍스처에 투명도 적용
                avatarModel.traverse((child) => {
                    if ((child as THREE.Mesh).isMesh) {
                        const mesh = child as THREE.Mesh;
                        if (mesh.material instanceof THREE.MeshStandardMaterial) {
                            mesh.material.transparent = true;
                            mesh.material.opacity = 0.4; // 원하는 투명도 설정
                        }
                    }
                });

                // 장비 모델 부착
                const category = modelCategory === 'hat' ? 'Head' : modelCategory === 'right-hand' ? 'rightHand' : modelCategory === 'left-hand' ? 'leftHand' : '';
                if (category) {
                    attachEquipment(
                        avatarModel,
                        modelUrl,
                        category
                    );
                }

                meshRef.current?.add(avatarModel);
                console.log('Avatar model added to the scene');
            } catch (error) {
                console.error(`An error occurred: ${error}`);
            }
        })();

        // 클린업 함수
        return () => {
            if (meshRef.current) {
                meshRef.current.remove(...meshRef.current.children);
            }
        };
    }, [modelUrl, modelCategory]);

    return <group ref={meshRef} rotation={[0, Math.PI, 0]}></group>;
};

export default MinecraftCharacterEquip;
